import React, { useState } from 'react';
import dayjs from 'dayjs';
import { TextField, Button } from '@mui/material';
import { RgbaColorPicker } from 'react-colorful';
import CustomDatePicker from '../../../components/UI/datepicker/DatePicker';
import BadgePreview from './BadgePreview';
import { rgbaObjToHex, hexToRgbaObj } from '../../../utils/colorUtils';

const COLOR_PRESETS = [
    { label: 'Регистрация открыта',   textColor: '#FFFFFFFF', backgroundColor: '#16EC25E3' },
    { label: 'Регистрация закрыта',   textColor: '#FFFFFFFF', backgroundColor: '#9EA59EE3' },
    { label: 'Регистрация скоро',     textColor: '#FFFFFFFF', backgroundColor: '#00BAFFE3' },
    { label: 'Регистрация дд.мм.гггг', textColor: '#FFFFFFFF', backgroundColor: '#FCB21FE3' },
    { label: 'Дни мероприятия',       textColor: '#FFFFFFFF', backgroundColor: '#FC3C1FE3' },
    { label: 'В эфире',               textColor: '#FFFFFFFF', backgroundColor: '#FC3C1FE3' },
];

const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: '#CEFC4C' },
        '&:hover fieldset': { borderColor: '#CEFC4C' },
        '&.Mui-focused fieldset': { borderColor: '#CEFC4C' },
        '&.Mui-error fieldset': { borderColor: '#f44336' },
        '& input': { color: '#fff' },
        '& textarea': { color: '#fff' },
    },
    '& .MuiInputLabel-root': {
        color: '#888',
        '&.Mui-focused': { color: '#fff' },
        '&.MuiFormLabel-filled': { color: '#fff' },
        '&.Mui-error': { color: '#f44336' },
    },
};

const BadgeForm = ({ initialValues, showDatePicker, onSave, onCancel }) => {
    const [text, setText] = useState(initialValues?.text || '');
    const [textColor, setTextColor] = useState(initialValues?.textColor || '#FFFFFFFF');
    const [backgroundColor, setBackgroundColor] = useState(initialValues?.backgroundColor || '#DC2626D9');
    const [activateAt, setActivateAt] = useState(initialValues?.activateAt || '');
    const [showTextColorPicker, setShowTextColorPicker] = useState(false);
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [textError, setTextError] = useState(false);
    const [activateAtError, setActivateAtError] = useState(false);

    const handlePresetClick = (preset) => {
        setText(preset.label);
        setTextColor(preset.textColor);
        setBackgroundColor(preset.backgroundColor);
        setTextError(false);
    };

    const handleSave = () => {
        if (!text.trim()) {
            setTextError(true);
            return;
        }
        if (showDatePicker) {
            if (!activateAt || dayjs(activateAt).isBefore(dayjs())) {
                setActivateAtError(true);
                return;
            }
        }
        onSave({ text: text.trim(), textColor, backgroundColor, ...(showDatePicker ? { activateAt } : {}) });
    };

    return (
        <div style={{ padding: 16, border: '1px solid #444', borderRadius: 8, marginTop: 12 }}>
            {/* Live preview */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>Предпросмотр:</div>
                <BadgePreview
                    text={text || 'Пример текста'}
                    textColor={textColor}
                    backgroundColor={backgroundColor}
                />
            </div>

            {/* Text input */}
            <TextField
                className='w-100'
                value={text}
                onChange={(e) => {
                    if (e.target.value.length <= 30) setText(e.target.value);
                    setTextError(false);
                }}
                label="Текст плашки"
                error={textError}
                helperText={textError ? 'Заполните текст плашки' : `${text.length}/30`}
                sx={textFieldStyles}
                FormHelperTextProps={{ sx: { color: textError ? '#f44336' : '#888' } }}
            />

            {/* Presets */}
            <div style={{ marginTop: 12, marginBottom: 12 }}>
                <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>Пресеты:</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {COLOR_PRESETS.map((preset) => (
                        <button
                            key={preset.label}
                            type="button"
                            onClick={() => handlePresetClick(preset)}
                            style={{
                                padding: '4px 12px',
                                borderRadius: 4,
                                backgroundColor: preset.backgroundColor,
                                color: preset.textColor,
                                fontSize: 12,
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Color pickers */}
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
                {/* Text color */}
                <div>
                    <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>Цвет текста:</div>
                    <div
                        onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 4,
                            backgroundColor: textColor,
                            border: '2px solid #666',
                            cursor: 'pointer',
                        }}
                    />
                    {showTextColorPicker && (
                        <div style={{ marginTop: 8 }}>
                            <RgbaColorPicker
                                color={hexToRgbaObj(textColor)}
                                onChange={(color) => setTextColor(rgbaObjToHex(color))}
                            />
                        </div>
                    )}
                </div>

                {/* Background color */}
                <div>
                    <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>Цвет фона:</div>
                    <div
                        onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 4,
                            backgroundColor: backgroundColor,
                            border: '2px solid #666',
                            cursor: 'pointer',
                        }}
                    />
                    {showBgColorPicker && (
                        <div style={{ marginTop: 8 }}>
                            <RgbaColorPicker
                                color={hexToRgbaObj(backgroundColor)}
                                onChange={(color) => setBackgroundColor(rgbaObjToHex(color))}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Date picker for scheduled badges */}
            {showDatePicker && (
                <div style={{ marginTop: 16, maxWidth: 300 }}>
                    <CustomDatePicker
                        label="Дата активации"
                        value={activateAt}
                        onChange={(isoDate) => { setActivateAt(isoDate); setActivateAtError(false); }}
                    />
                    {activateAtError && (
                        <span style={{ color: '#f44336', fontSize: 12, marginTop: 4, display: 'block' }}>
                            Укажите дату в будущем
                        </span>
                    )}
                </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
                <Button variant="contained" onClick={handleSave}
                    sx={{ textTransform: 'none', backgroundColor: '#CEFC4C', color: '#000', '&:hover': { backgroundColor: '#b8e043' } }}>
                    Сохранить
                </Button>
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    sx={{ textTransform: 'none', color: '#fff', borderColor: '#666', '&:hover': { borderColor: '#888' } }}
                >
                    Отмена
                </Button>
            </div>
        </div>
    );
};

export default BadgeForm;
